import { useSessionStore, labelMap } from './SessionStore';
import { useState, useRef, useEffect } from 'react';
import { useSiteSettings } from './context/SiteSettingsContext';

export type NoteHeaderFieldId =
    | 'name'
    | 'type'
    | 'template'
    | 'date'
    | 'duration'
    | 'recording'
    | 'gender'
    | 'age'
    | 'startStopTime';

type NoteHeaderEntry = {
    id: NoteHeaderFieldId;
    label: string;
    value: string;
    group: 'left' | 'right';
};

const NOTE_HEADER_FIELD_ORDER: NoteHeaderFieldId[] = [
    'name',
    'type',
    'gender',
    'age',
    'template',
    'date',
    'duration',
    'recording',
    'startStopTime',
];

export const DEFAULT_NOTE_HEADER_FIELDS: NoteHeaderFieldId[] = [
    'name',
    'type',
    'template',
    'date',
    'duration',
    'recording',
];

const NOTE_HEADER_FIELD_LABELS: Record<NoteHeaderFieldId, string> = {
    name: 'Name',
    type: 'Typ',
    template: 'Template',
    date: 'Datum',
    duration: 'Dauer',
    recording: 'Aufnahme',
    gender: 'Geschlecht',
    age: 'Alter',
    startStopTime: 'Start-/Stoppzeit',
};

const NOTE_HEADER_FIELD_GROUPS: Record<NoteHeaderFieldId, 'left' | 'right'> = {
    name: 'left',
    type: 'left',
    gender: 'left',
    age: 'left',
    template: 'right',
    date: 'right',
    duration: 'right',
    recording: 'right',
    startStopTime: 'right',
};

const getNormalizedNoteHeaderFields = (fields?: NoteHeaderFieldId[]) => {
    const fallback = DEFAULT_NOTE_HEADER_FIELDS;
    const selected = new Set<NoteHeaderFieldId>(
        Array.isArray(fields) && fields.length > 0
            ? fields.filter((field): field is NoteHeaderFieldId => NOTE_HEADER_FIELD_ORDER.includes(field))
            : fallback
    );

    selected.add('name');
    selected.add('date');

    return NOTE_HEADER_FIELD_ORDER.filter((field) => selected.has(field));
};

const getValidSessionDate = (dateValue?: string) => {
    if (!dateValue) return null;
    const parsedDate = new Date(dateValue);
    if (Number.isNaN(parsedDate.getTime())) {
        return null;
    }
    return parsedDate;
};

const formatDateValue = (dateValue?: string, format: 'long' | 'short' = 'long') => {
    const parsedDate = getValidSessionDate(dateValue);
    if (!parsedDate) {
        return 'Unbekannt';
    }

    if (format === 'short') {
        return `${String(parsedDate.getDate()).padStart(2, '0')}.${String(parsedDate.getMonth() + 1).padStart(2, '0')}.${parsedDate.getFullYear()}`;
    }

    return parsedDate.toLocaleDateString('de', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
};

const formatStartStopTime = (dateValue?: string, lengthValue?: string | number) => {
    const startDate = getValidSessionDate(dateValue);
    const sessionLengthInSeconds = Number(lengthValue);

    if (!startDate || !Number.isFinite(sessionLengthInSeconds) || sessionLengthInSeconds <= 0) {
        return null;
    }

    const endDate = new Date(startDate.getTime() + sessionLengthInSeconds * 1000);
    const formatter = (value: Date) =>
        value.toLocaleTimeString('de-DE', {
            hour: '2-digit',
            minute: '2-digit',
        });

    return `${formatter(startDate)} - ${formatter(endDate)}`;
};

export const getNoteHeaderEntries = (
    sessionData: any,
    fields: NoteHeaderFieldId[] | undefined,
    mode: 'session' | 'case',
    options?: { dateFormat?: 'long' | 'short' }
): NoteHeaderEntry[] => {
    if (!sessionData) {
        return [];
    }

    const normalizedFields = getNormalizedNoteHeaderFields(fields);
    const dateFormat = options?.dateFormat ?? 'long';

    return normalizedFields.flatMap((fieldId) => {
        if (mode === 'case' && (fieldId === 'type' || fieldId === 'duration')) {
            return [];
        }

        const templateValue = sessionData.documentation?.template_used || sessionData.template_used || '';
        const startStopTime = formatStartStopTime(sessionData.date, sessionData.length);

        const valueByField: Record<NoteHeaderFieldId, string | null> = {
            name: sessionData.name || '',
            type: labelMap[sessionData.participation_type || ''] || '',
            template: templateValue,
            date: formatDateValue(sessionData.date, dateFormat),
            duration: Number.isFinite(Number(sessionData.length)) ? `${Math.round(Number(sessionData.length) / 60)} minuten` : '',
            recording: labelMap[sessionData.recording_type || ''] || '',
            gender: labelMap[sessionData.gender || ''] || '',
            age: sessionData.child_age !== null && sessionData.child_age !== undefined ? String(sessionData.child_age) : null,
            startStopTime,
        };

        const value = valueByField[fieldId];
        if (!value) {
            return [];
        }

        return [{
            id: fieldId,
            label: NOTE_HEADER_FIELD_LABELS[fieldId],
            value,
            group: NOTE_HEADER_FIELD_GROUPS[fieldId],
        }];
    });
};

type SessionDetailsProps = {
    mode: "session" | "case";
    noteHeaderFields?: NoteHeaderFieldId[];
};

const SessionDetails: React.FC<SessionDetailsProps> = ({ mode, noteHeaderFields }) => {
    const { sessionData, updateSession } = useSessionStore();
    const [editedTitle, setEditedTitle] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const { settings: siteSettings, loading: isSiteSettingsLoading } = useSiteSettings();
    const logLevel = siteSettings?.frontend_log_level ?? 0;

    const isModeCase = mode === "case";




    useEffect(() => {
        // Editiermodus verlassen und Feld zurücksetzen bei Sessionwechsel
        setIsEditing(false);
        setEditedTitle('');
    }, [sessionData?.id]);


    useEffect(() => {
        if (isEditing && inputRef.current) {
            // kleiner Delay, damit DOM-Update abgeschlossen ist
            setTimeout(() => {
                inputRef.current?.focus();
                inputRef.current?.select();
            }, 0);
        }

    }, [isEditing]);

    if (!sessionData) return <p>Keine Sitzung ausgewählt</p>;

    const noteHeaderEntries = getNoteHeaderEntries(sessionData, noteHeaderFields, mode);
    const noteHeaderColumnCount = Math.min(5, Math.max(3, Math.ceil(noteHeaderEntries.length / 2)));

    const handleSaveTitle = async () => {
        const success = await updateSession(sessionData.id, { name: editedTitle });
        if (success) {
            if (!isSiteSettingsLoading && logLevel >= 3) console.log('✅ Titel erfolgreich aktualisiert:', editedTitle);
            setIsEditing(false);
            setEditedTitle('');
        } else {
            if (!isSiteSettingsLoading && logLevel >= 1) console.error('❌ Fehler beim Aktualisieren des Titels');
        }
    };



    return (
        <div id="onboarding-step-four" className="bg-white rounded-lg">
            <div className="_noteHeader_1iydr_1">
                <div className="_clientDetails_1iydr_43 !tw-w-full">
                    <div className="_initDetails_1iydr_70">
                        <div className="_clientHeader_1iydr_54">

                            <button
                                className={`_button_19rto_40 _primary_19rto_127 _ghost_19rto_149 !font-normal !text-xs !p-0 !m-0 ${isModeCase ? '!text-[#E7A555]' : 'text-primary-500'
                                    }`}
                                onClick={() => {
                                    if (isEditing) {
                                        handleSaveTitle();
                                    } else {
                                        setIsEditing(true);
                                        setEditedTitle(sessionData.name);
                                    }
                                }}
                            >
                                <div className="_btnFlex_19rto_32">
                                    {isEditing ? 'Speichern' : isModeCase ? 'Berichtdetails' : 'Sitzungsdetails'}
                                    <div className="_btnAddon_19rto_56 _btnRightAddon_19rto_65">
                                        <div className="_btnAddon_19rto_56 _btnRightAddon_19rto_65">
                                            {isEditing ? (
                                                // Disketten-/Speicher-Symbol
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M17 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7l-4-4zM12 19a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm3-10H5V5h10v4z" fill={isModeCase ? "#E7A555" : "#32768D"} />
                                                </svg>
                                            ) : (
                                                // Dein aktuelles Stift-SVG
                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"
                                                    xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M3.69723 13.0156C3.29056 13.0156 2.91056 12.8756 2.63723 12.6156C2.29056 12.289 2.1239 11.7956 2.1839 11.2623L2.43056 9.10231C2.47723 8.69564 2.7239 8.15565 3.01056 7.86231L8.4839 2.06898C9.85056 0.622311 11.2772 0.582311 12.7239 1.94898C14.1706 3.31564 14.2106 4.74231 12.8439 6.18898L7.37056 11.9823C7.09056 12.2823 6.57056 12.5623 6.1639 12.629L4.01723 12.9956C3.9039 13.0023 3.8039 13.0156 3.69723 13.0156Z" fill={isModeCase ? "#E7A555" : "#32768D"} />
                                                    <path d="..." fill={isModeCase ? "#E7A555" : "#32768D"} />
                                                </svg>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </button>


                        </div>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="cursor-pointer">
                            <path d="M8 10C9.10457 10 10 9.10457..." stroke="#32768D"></path>
                            <path d="M1.33594 8.59021V7.41687..." stroke="#32768D"></path>
                        </svg>
                    </div>
                    <div
                        className="_clientGroup_1iydr_77"
                        style={{
                            display: 'grid',
                            gridTemplateColumns: `repeat(${noteHeaderColumnCount}, minmax(0, 1fr))`,
                            gridAutoFlow: 'row',
                            gap: '1.0rem 3rem',
                            width: '100%',
                        }}
                    >
                        {noteHeaderEntries.map((entry) => (
                            <div key={entry.id} className="_eachGroup_1iydr_93">
                                <span className="_clientLabel_1iydr_97 !font-bold">{entry.label}</span>
                                {entry.id === 'name' && isEditing ? (
                                    <input
                                        className="_clientValue_1iydr_104 border border-primary-500 rounded px-1"
                                        value={editedTitle}
                                        onChange={(e) => setEditedTitle(e.target.value)}
                                        ref={inputRef}
                                    />
                                ) : (
                                    <span className="_clientValue_1iydr_104">{entry.value}</span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SessionDetails;
